/**
 * quoteDraft.js - the quote builder's pure form logic (tested by
 * __tests__/quoteDraft.test.js): a new draft prefilled from the opportunity,
 * its account / site and contacts and the signed-in person; and the calendar
 * edits (click, type, paint, week override, reset). The arithmetic is NOT
 * here - every hour and dollar comes from the server's quote engine
 * (POST /api/quotes/calc, server/lib/quoteEngine.js), so the screen and the
 * document can never disagree.
 *
 * A day's edit is stored as { k: 'W'|'T'|'X', h? }: no h on a work day means
 * "its week's hours" (the engine works them out); a typed number is kept.
 * The combined calendar writes `days` (everyone) and clears that date from
 * every engineer's own calendar, as the workbook's single grid did; an
 * engineer's own calendar writes `slotDays[n]`, which beats `days`.
 */
import { contactsForAccount, indexById } from '../crm/nextStep';
import { pickField } from '../../data/crmStages';
import { localDay } from '../../utils/localDay';

export const MAX_ENGINEERS = 8;
export const NEXT_KIND = { W: 'T', T: 'X', X: 'W' };

const txt = (v) => (v === null || v === undefined ? '' : String(v).trim());
const sameName = (a, b) => txt(a).toLowerCase() === txt(b).toLowerCase();

/** Today as 'YYYY-MM-DD' in local time. */
export function todayIso(now = new Date()) {
  return localDay(now);
}

/**
 * The customer library entry for a name (the workbook's Rates sheet): an
 * exact match, else one whose name starts with the opportunity's customer
 * ("FANUC" finds "FANUC America, Inc."), else null.
 */
export function libraryCustomer(settings, name) {
  const list = (settings && settings.customers) || [];
  const n = txt(name).toLowerCase();
  if (!n) return null;
  return (
    list.find((c) => sameName(c.name, n)) ||
    list.find((c) => txt(c.name).toLowerCase().startsWith(n) || n.startsWith(txt(c.name).toLowerCase())) ||
    null
  );
}

/**
 * What the opportunity and the CRM know about who the quote is for:
 * { customer, addr1, addr2, attention, contactEmail, location }. The company
 * name is the site's parent company when the opportunity is linked to a site.
 */
/** A field off the editor's live form when there is one (blank included), else off the saved record. */
const fromForm = (form, opp, key, snake) => (form && key in form ? form[key] : pickField(opp || {}, key, snake));

export function customerFromCrm({ opp, form, accounts }) {
  const byId = indexById(accounts || []);
  const accountId = txt(fromForm(form, opp, 'accountId', 'account_id'));
  const acct = accountId ? byId.get(accountId) : null;
  const parentId = acct ? pickField(acct, 'parentAccountId', 'parent_account_id') : null;
  const parent = parentId ? byId.get(String(parentId)) : null;
  const contacts = accountId ? contactsForAccount(accountId, byId) : [];
  const att = contacts[0] || null;
  return {
    customer: txt((parent && parent.name) || (acct && !parentId && acct.name) || fromForm(form, opp, 'customer', 'customer')),
    location: txt((acct && acct.location) || (parent && parent.location)),
    attention: att ? txt(att.name) : '',
    contactEmail: att ? txt(att.email) : '',
  };
}

/** The header both quote types share, prefilled. Anything can be overtyped later. */
export function commonHeader({ opp, form, accounts, user, settings, type }) {
  const o = opp || {};
  const defs = (settings && settings.defaults) || {};
  const company = (settings && settings.company) || {};
  const crm = customerFromCrm({ opp, form, accounts });
  const lib = libraryCustomer(settings, crm.customer);
  const prefix = txt(settings && settings.offerPrefixes && settings.offerPrefixes[type]);
  const quote = txt(fromForm(form, o, 'quoteLetter', 'quote_letter'));
  // Name (the old topic only where a record never had a name)
  const title = form ? form.name || form.topic || form.description : o.name || o.topic || o.description;
  return {
    offerNo: quote ? `${prefix}${quote}` : '',
    offerDate: todayIso(),
    rfqNo: txt(fromForm(form, o, 'customerProjectNumber', 'customer_project_number')),
    subject: txt(title),
    customer: (lib && lib.name) || crm.customer,
    addr1: lib ? txt(lib.addr1) : '',
    addr2: lib ? txt(lib.addr2) : crm.location,
    attention: crm.attention || (lib ? txt(lib.buyer) : ''),
    preparer: txt(user && user.name),
    prepEmail: txt(user && user.email),
    prepPhone: txt(company.phone),
    payTerms: (lib && txt(lib.payTerms)) || txt(defs.payTerms),
    validity: String(defs.validityDays || 30),
    lib,
  };
}

/** A new Service Quote draft for this opportunity. */
export function newServiceDraft(ctx) {
  const { settings } = ctx;
  const defs = (settings && settings.defaults) || {};
  const h = commonHeader({ ...ctx, type: 'service' });
  const { lib } = h;
  delete h.lib;
  return {
    header: {
      ...h,
      rev: 0,
      kicker: txt(defs.kicker) || 'SERVICE PROPOSAL',
      salutation: (lib && txt(lib.salutation)) || h.attention,
      invoicing: (lib && txt(lib.invoicing)) || txt(defs.invoicing),
      poType: txt(defs.poType) || 'T&L',
      poNumber: '',
      rateGroup: (lib && txt(lib.rateGroup)) || '',
      // a card with no default site (GLOBAL KSA's starts with no locations) leaves it blank
      siteLocation: (lib && txt(lib.siteLocation)) || ('siteLocation' in defs ? txt(defs.siteLocation) : 'Michigan'),
      endUser: '',
      // a field change order: its previous overages go into the TOTAL BUDGET (quoteEngine.js)
      fco: false,
      prevOverages: '',
      overagesNote: '',
      fcoRef: '',
    },
    // the workbook's New Quote: 2 engineers, 8-hour weekdays, weekends off
    schedule: { start: '', end: '', engineers: 2, wd: 8, sat: 0, sun: 0, weekOverrides: {}, days: {}, slotDays: {} },
    team: syncTeam([], 2, settings),
    other: [],
    expenses: { overrides: {}, mileage: 0 },
    option: [],
    scope: '',
  };
}

/** A new Parts Quote draft for this opportunity. */
export function newPartsDraft(ctx) {
  const { settings } = ctx;
  const defs = (settings && settings.defaults) || {};
  const h = commonHeader({ ...ctx, type: 'parts' });
  delete h.lib;
  return {
    header: {
      ...h,
      subject: h.subject || txt(defs.partsSubject),
      shipName: '',
      shipAddr1: '',
      shipAddr2: '',
      shipVia: txt(defs.shipVia) || 'Courier',
      freightTerms: txt(defs.freightTerms),
      leadNote: txt(defs.leadNote) || 'Per line, ARO.',
      warranty: txt(defs.warranty),
      discountPct: '',
      freightAmount: '',
    },
    lines: [blankPart()],
    notes: '',
  };
}

export const blankPart = () => ({ partNo: '', manufacturer: '', description: '', unit: '', leadTime: '', qty: 1, price: '' });
export const blankLine = () => ({ task: '', desc: '', billType: 'T&L', rate: 'NT', hours: '' });

/** A task's default expense package (the Rates sheet's task list), 'None' if it has none. */
export function defaultPackageFor(settings, task) {
  const t = ((settings && settings.tasks) || []).find((x) => sameName(x.name, task));
  return (t && txt(t.defaultPackage)) || 'None';
}

/**
 * One team row per engineer on the calendar (modSchedule.FillTeamRoles): new
 * rows are "Engineer n", T&L, with the task's default expense package written
 * in so a quote with travel days cannot quietly come out with no expenses.
 */
export function syncTeam(team, engineers, settings) {
  const n = Math.max(1, Math.min(MAX_ENGINEERS, Number(engineers) || 1));
  const out = [];
  for (let i = 0; i < n; i += 1) {
    const r = (team || [])[i] || {};
    const task = txt(r.task);
    out.push({
      role: txt(r.role) || `Engineer ${i + 1}`,
      task,
      billType: txt(r.billType) || 'T&L',
      pkg: txt(r.pkg) || defaultPackageFor(settings, task),
    });
  }
  return out;
}

/** A team row's task changed: the Expenses package follows it unless someone picked one. */
export function setTeamTask(row, task, settings) {
  const wasDefault = !txt(row.pkg) || sameName(row.pkg, defaultPackageFor(settings, row.task));
  return { ...row, task, pkg: wasDefault ? defaultPackageFor(settings, task) : row.pkg };
}

/* ── the calendar ──────────────────────────────────────────────────────── */

/** The schedule with one date's edit set (value null = back to the week table). slot 0 = everyone. */
export function setDay(schedule, date, value, slot = 0) {
  const s = { ...schedule, days: { ...(schedule.days || {}) }, slotDays: { ...(schedule.slotDays || {}) } };
  if (slot > 0) {
    const mine = { ...(s.slotDays[slot] || {}) };
    if (value) mine[date] = value;
    else delete mine[date];
    s.slotDays[slot] = mine;
    return s;
  }
  if (value) s.days[date] = value;
  else delete s.days[date];
  // the one grid for everyone sets every engineer to match it
  for (const k of Object.keys(s.slotDays)) {
    if (s.slotDays[k] && s.slotDays[k][date]) {
      const mine = { ...s.slotDays[k] };
      delete mine[date];
      s.slotDays[k] = mine;
    }
  }
  return s;
}

/** Many dates at once (a paint drag). */
export const setDays = (schedule, dates, value, slot = 0) => dates.reduce((s, d) => setDay(s, d, value, slot), schedule);

/** Click: work -> travel -> off -> work. `kind` is what the day shows now. */
export const cycleDay = (schedule, date, kind, slot = 0) => setDay(schedule, date, { k: NEXT_KIND[kind] || 'W' }, slot);

/** Typed hours: 0 turns the day off, more works that many; nonsense changes nothing. */
export function typeHours(schedule, date, raw, slot = 0) {
  const t = txt(raw);
  if (t === '') return setDay(schedule, date, { k: 'X' }, slot);
  const h = Number(t);
  if (!Number.isFinite(h) || h < 0 || h > 24) return schedule;
  return setDay(schedule, date, h === 0 ? { k: 'X' } : { k: 'W', h }, slot);
}

/** A week's Mon-Fri / Sat / Sun override (blank = back to the boxes). */
export function setWeekOverride(schedule, monday, key, raw) {
  const over = { ...(schedule.weekOverrides || {}) };
  const row = { ...(over[monday] || {}) };
  const t = txt(raw);
  const h = Number(t);
  if (t === '') delete row[key];
  else if (Number.isFinite(h) && h >= 0 && h <= 24) row[key] = h;
  else return schedule;
  if (Object.keys(row).length) over[monday] = row;
  else delete over[monday];
  return { ...schedule, weekOverrides: over };
}

/** Reset: forget every clicked day and every changed week; the boxes fill everything again. */
export const resetSchedule = (schedule) => ({ ...schedule, weekOverrides: {}, days: {}, slotDays: {} });

/** How many hand-set days and changed weeks there are (for the Reset button's label). */
export function editCount(schedule) {
  const s = schedule || {};
  let n = Object.keys(s.days || {}).length + Object.keys(s.weekOverrides || {}).length;
  for (const v of Object.values(s.slotDays || {})) n += Object.keys(v || {}).length;
  return n;
}

/** The months a date range spans: [{ year, month (0-11) }]. */
export function monthsBetween(start, end) {
  const a = /^(\d{4})-(\d{2})/.exec(start || '');
  const b = /^(\d{4})-(\d{2})/.exec(end || '');
  if (!a || !b || end < start) return [];
  const out = [];
  let y = +a[1];
  let m = +a[2] - 1;
  while (y < +b[1] || (y === +b[1] && m <= +b[2] - 1)) {
    out.push({ year: y, month: m });
    m += 1;
    if (m === 12) {
      m = 0;
      y += 1;
    }
    if (out.length > 12) break;
  }
  return out;
}

export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const isDow = (v) => Number.isInteger(v) && v >= 0 && v <= 6;

/**
 * The rate card's work week, as the server's quote engine reads it (weekOf
 * in server/lib/quoteEngine.js): weekStart 0 Sunday .. 6 Saturday (default 1
 * Monday), dtDays the always-double-time weekdays (default [0] Sunday). From
 * the full card (settings.rates) or the builder's view of it (settings.week).
 * labels names the three hour boxes: 'sun' the DT day(s), 'sat' the day
 * before the first one, 'wd' the rest - Mon-Fri / Saturday / Sunday on the
 * main card, Sat-Wed / Thursday / Friday on GLOBAL KSA's. gridStart is the
 * calendar's first column: Sunday on the main card, as the workbook, else
 * the day the week starts. The engine alone works out the tiers.
 */
export function weekOfSettings(settings) {
  const r = (settings && (settings.rates || settings.week)) || {};
  const ws = r.weekStart === '' || r.weekStart === null || r.weekStart === undefined ? NaN : Number(r.weekStart);
  const weekStart = isDow(ws) ? ws : 1;
  const dtDays = Array.isArray(r.dtDays) ? [...new Set(r.dtDays.map(Number).filter(isDow))] : [0];
  const first = dtDays.length ? dtDays[0] : null;
  const satDay = first === null || dtDays.includes((first + 6) % 7) ? null : (first + 6) % 7;
  const boxOf = (d) => (dtDays.includes(d) ? 'sun' : d === satDay ? 'sat' : 'wd');
  const order = Array.from({ length: 7 }, (_, i) => (weekStart + i) % 7);
  const wdDays = order.filter((d) => boxOf(d) === 'wd');
  const short = (d) => DAY_NAMES[d].slice(0, 3);
  const follows = wdDays.every((d, i) => i === 0 || order.indexOf(d) === order.indexOf(wdDays[i - 1]) + 1);
  const wd = !wdDays.length
    ? 'Weekdays'
    : wdDays.length === 1
      ? short(wdDays[0])
      : follows
        ? `${short(wdDays[0])}-${short(wdDays[wdDays.length - 1])}`
        : wdDays.map(short).join(', ');
  return {
    weekStart,
    dtDays,
    satDay,
    gridStart: weekStart === 1 ? 0 : weekStart,
    labels: {
      wd,
      sat: satDay === null ? '' : DAY_NAMES[satDay],
      sun: dtDays.map((d) => DAY_NAMES[d]).join(' / '),
      weekStart: DAY_NAMES[weekStart],
    },
    isDefault: weekStart === 1 && dtDays.length === 1 && dtDays[0] === 0,
  };
}

/**
 * A month as rows of 7 'YYYY-MM-DD' (or null) cells, Sunday first like the
 * workbook - or starting on `firstDow` (0-6; GLOBAL KSA's Saturday).
 */
export function monthGrid(year, month, firstDow = 0) {
  const first = (new Date(Date.UTC(year, month, 1)).getUTCDay() - firstDow + 7) % 7;
  const days = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const p = (n) => String(n).padStart(2, '0');
  const cells = [];
  for (let i = 0; i < 42; i += 1) {
    const d = i - first + 1;
    cells.push(d >= 1 && d <= days ? `${year}-${p(month + 1)}-${p(d)}` : null);
  }
  const rows = [];
  for (let r = 0; r < 6; r += 1) rows.push(cells.slice(r * 7, r * 7 + 7));
  return rows.filter((r) => r.some(Boolean));
}

/** A draft safe to save: the team trimmed to the engineers on the calendar. */
export function cleanServiceDraft(d, settings) {
  return { ...d, team: syncTeam(d.team, d.schedule && d.schedule.engineers, settings) };
}
